import { Form as FinalForm, Field } from 'react-final-form'
import { useTranslation } from 'react-i18next'
import { styled, Theme } from '@mui/material/styles'
import { makeStyles } from '@mui/styles'
import { Grid, Typography, Button, Divider } from '@mui/material'
import Dialog from '../../control/Dialog'
import Textbox from '../../control/Textbox'
import useUserGroup from '../../hooks/backoffice/useUserGroup'
import DataTable, { ColumnSettingType, RenderColumnAction } from '../../control/dataTable/DataTable'
import EditAction from '../../control/dataTable/renderAction/EditAction'
import DeleteAction from '../../control/dataTable/renderAction/DeleteAction'
import { ReactComponent as AddIcon } from '../../icon/backoffice_add.svg'

type ManageUserGroupProps = {}
const ManageUserGroup = (props: ManageUserGroupProps) => {
    const {
        onSubmitSearch,
        onChangePageNumber,
        onSort,
        onClickAdd,
        onClickEdit,
        onClickDelete,
        onClickOkDelete,
        onClickCancelDelete,
        onClickConfirmDelete,
        data,
        totalItem,
        paramServerSide,
        openDialogDelete,
        openDialogOkDelete,
    } = useUserGroup()
    const { t } = useTranslation()
    const classes = useStyles()

    const columnOptions: ColumnSettingType[] = [
        {
            dataKey: 'no',
            label: 'ลำดับ',
            align: 'center',
        },
        {
            dataKey: 'groupName',
            label: 'ชื่อกลุ่ม',
            align: 'left',
        },
        {
            dataKey: 'description',
            label: 'รายละเอียด',
            align: 'left',
        },
        {
            dataKey: 'noOfUsers',
            label: 'จำนวนผู้ใช้งาน',
            align: 'center',
        },
        {
            dataKey: 'action',
            label: 'จัดการ',
            align: 'center',
            type: 'action',
            render: (row: any) => (
                <RenderColumnAction>
                    <EditAction data={row} onClick={(data: any) => onClickEdit(data)} />
                    {row?.noOfUsers === 0 && (
                        <>
                            <Divider sx={{ height: 16 }} orientation="vertical" />
                            <DeleteAction data={row} onClick={(data: any) => onClickDelete(data)} />
                        </>
                    )}
                </RenderColumnAction>
            ),
        },
    ]

    return (
        <>
            <StyledDivHeader>
                <StyledTypoHeader variant="h6">{t('MENU.USER_GROUP_MANAGEMENT')}</StyledTypoHeader>
            </StyledDivHeader>
            <StyledDivSearch>
                <FinalForm onSubmit={onSubmitSearch}>
                    {({ handleSubmit, values }) => (
                        <form onSubmit={handleSubmit}>
                            <Grid container spacing={2} className={classes.container}>
                                <Grid item xs={12} md={9} lg={10}>
                                    <Field
                                        name="keyword"
                                        label={t('LABEL.BACKOFFICE.SEARCH_KEYWORD')}
                                        placeholder={t('LABEL.BACKOFFICE.PLACEHOLDER_USER_GROUP')}
                                        component={Textbox}
                                        fullWidth
                                        onKeyUp={(e: any) => {
                                            if (e.keyCode == 13) {
                                                handleSubmit()
                                            }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={3} lg={2}>
                                    <div className={classes.divButton}>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={handleSubmit}
                                            classes={{ root: classes.buttonSearch }}
                                        >
                                            {t('BUTTON.SEARCH')}
                                        </Button>
                                    </div>
                                </Grid>
                            </Grid>
                        </form>
                    )}
                </FinalForm>
            </StyledDivSearch>
            <StyledDivContent>
                <StyledDivSearchResultHeader>
                    <StyledDivSearchResultTotal>
                        <StyledTypoSearchResultTitle>{`รายการ${t(
                            'LABEL.BACKOFFICE.USER_GROUP.TITLE',
                        )}`}</StyledTypoSearchResultTitle>
                        <StyledTypoSearchResultAmount>{`(${totalItem} รายการ)`}</StyledTypoSearchResultAmount>
                    </StyledDivSearchResultTotal>
                    <StyledDivAddData>
                        <Button onClick={onClickAdd}>
                            <AddIcon />
                            <StyledTypoAdd>{`${t('BUTTON.ADD')}${t(
                                'LABEL.BACKOFFICE.USER_GROUP.TITLE',
                            )}`}</StyledTypoAdd>
                        </Button>
                    </StyledDivAddData>
                </StyledDivSearchResultHeader>
                <DataTable<any>
                    columnOptions={columnOptions}
                    dataSource={data}
                    maxHeight={500}
                    onRowClick={(e) => {}}
                    onRowPerPage={() => {}}
                    onPageNumber={onChangePageNumber}
                    totalItem={totalItem}
                    rowsPerPage={totalItem >= paramServerSide.rowPerPage ? paramServerSide.rowPerPage : totalItem}
                    onSort={onSort}
                    sortField={paramServerSide.sortField}
                    sortType={paramServerSide.sortType}
                    pageNumber={paramServerSide.pageNumber}
                />
            </StyledDivContent>
            <Dialog
                type="confirm"
                title={t('ALERT.TITLE.NOTIFY')}
                content={'ต้องการลบข้อมูลหรือไม่'}
                isOpen={openDialogDelete}
                onClickConfirm={onClickConfirmDelete}
                onClickCancel={onClickCancelDelete}
                labelButtonCancel={t('BUTTON.CANCEL')}
                labelButtonConfirm={t('BUTTON.CONFIRM')}
                disableClickBackdrop
                hideCloseButton
            />
            <Dialog
                type="ok"
                title={t('ALERT.TITLE.NOTIFY')}
                content={'ลบข้อมูลสำเร็จ'}
                isOpen={openDialogOkDelete}
                onClickOk={onClickOkDelete}
                labelButtonOk={t('BUTTON.OK')}
                disableClickBackdrop
                hideCloseButton
            />
        </>
    )
}

export default ManageUserGroup

const useStyles = makeStyles((theme: Theme) => ({
    container: {
        display: 'flex',
        justifyContent: 'space-between',
    },
    divButton: {
        [theme.breakpoints.up('md')]: {
            paddingTop: '24px',
        },
        [theme.breakpoints.down('md')]: {
            display: 'flex',
            justifyContent: 'end',
        },
    },
    buttonSearch: {
        width: '125px',
        minHeight: '37px',
        fontSize: '14px !important',
    },
    buttonAdd: {
        minWidth: '28px !important',
        minHeight: '28px !important',
        padding: '0px !important',
    },
}))

const StyledDivHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    paddingBottom: '15px',
}))

const StyledTypoHeader = styled(Typography)(({ theme }) => ({
    fontWeight: 'bold',
}))

const StyledDivSearch = styled('div')(({ theme }) => ({
    width: '100%',
    padding: '15px 30px',
    marginBottom: '15px',
    borderRadius: '5px',
    backgroundColor: theme.palette.text.secondary,
    boxShadow: theme.palette.getContrastText('shadow-bottom-gray'),
    [theme.breakpoints.down('lg')]: {
        width: '85%',
    },
}))

const StyledDivContent = styled('div')(({ theme }) => ({
    width: '100%',
    padding: '20px 30px',
    marginTop: '15px',
    borderRadius: '5px',
    backgroundColor: theme.palette.text.secondary,
    boxShadow: theme.palette.getContrastText('shadow-bottom-gray'),
    [theme.breakpoints.down('lg')]: {
        width: '85%',
    },
}))

const StyledDivSearchResultHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    marginBottom: '10px',
    alignItems: 'center',
    justifyContent: 'space-between',
}))

const StyledDivSearchResultTotal = styled('div')(({ theme }) => ({
    display: 'flex',
}))

const StyledTypoSearchResultTitle = styled(Typography)(({ theme }) => ({
    marginRight: '5px',
    fontWeight: 'bold',
    color: theme.palette.secondary.dark,
}))

const StyledTypoSearchResultAmount = styled(Typography)(({ theme }) => ({
    color: theme.palette.primary.main,
}))

const StyledDivAddData = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    marginRight: '15px',
}))

const StyledTypoAdd = styled(Typography)(({ theme }) => ({
    marginLeft: '10px',
    fontWeight: 'bold',
    color: theme.palette.secondary.dark,
}))
